import { useEffect, useState } from "react";
import { Dialog } from "../../../../components/common/dialog";
import { Input } from "../../../../components/common/input";
import { DatePicker } from "../../../../components/common/date-picker";
import { Text } from "../../../../components/common/typography";
import { Button } from "../../../../components/common/button";
import { usePayVendorBill } from "../../hooks/useSupplierUnpaidBills";
import type { VendorBillDto } from "../../data/types";

interface Props {
  bill: VendorBillDto | null;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export function PayVendorBillDialog({ bill, open, onClose, onSuccess }: Props) {
  const { pay, isLoading } = usePayVendorBill();
  const [amount, setAmount] = useState<string>("");
  const [date, setDate] = useState<string>(todayIso());
  const [memo, setMemo] = useState<string>("");
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Reset whenever the dialog opens for a new bill.
  useEffect(() => {
    if (open && bill) {
      setAmount(String(bill.amountResidual ?? 0));
      setDate(todayIso());
      setMemo("");
      setSubmitError(null);
    }
  }, [open, bill]);

  if (!bill) return null;

  const residual = bill.amountResidual ?? 0;
  const numericAmount = Number(amount);
  const amountValid =
    Number.isFinite(numericAmount) && numericAmount > 0 && numericAmount <= residual;

  const handleConfirm = async () => {
    if (!amountValid) return;
    try {
      await pay({
        billId: bill.id,
        amount: numericAmount,
        paymentDate: date,
        memo: memo.trim() || undefined,
      });
      onSuccess?.();
      onClose();
    } catch (err: any) {
      setSubmitError(
        err?.response?.data?.message ??
          err?.message ??
          "Failed to register payment. Please try again.",
      );
    }
  };

  const billRef = bill.reference ?? `Bill #${bill.id}`;
  const currency = bill.currency ?? "QAR";

  return (
    <Dialog
      open={open}
      setOpen={(v) => !v && onClose()}
      title={`Pay ${billRef}`}
      titleSize="lg"
      titleAlignment="left"
      showCloseButton
      showButtons={false}
      width="max-w-md"
      content={
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1 rounded-xl border border-borderDefault bg-bgLight px-4 py-3">
            <Text variant="bodyXs" tone="muted">Supplier</Text>
            <Text variant="bodySmBold" tone="dark">
              {bill.supplierName ?? "—"}
            </Text>
            <div className="mt-2 grid grid-cols-2 gap-3">
              <div>
                <Text variant="bodyXs" tone="muted">Outstanding</Text>
                <Text variant="bodySmBold" tone="dark">
                  {residual.toLocaleString()} {currency}
                </Text>
              </div>
              <div>
                <Text variant="bodyXs" tone="muted">Total</Text>
                <Text variant="bodySm" tone="body">
                  {(bill.amountTotal ?? 0).toLocaleString()} {currency}
                </Text>
              </div>
            </div>
          </div>

          <Input
            label={`Amount (${currency})`}
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            error={
              amount && !amountValid
                ? `Must be between 0 and ${residual.toLocaleString()}`
                : undefined
            }
            min={0}
            max={residual}
            step="0.01"
          />

          <DatePicker
            label="Payment date"
            value={date}
            onChange={setDate}
            size="lg"
          />

          <Input
            label="Memo (optional)"
            value={memo}
            onChange={(e) => setMemo(e.target.value)}
            placeholder="e.g. wire ref, batch id…"
          />

          {submitError && (
            <Text variant="bodySm" tone="inherit" className="text-accentError">
              {submitError}
            </Text>
          )}

          <div className="mt-2 flex items-center justify-end gap-3">
            <Button variant="secondary" size="sm" onClick={onClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleConfirm}
              disabled={!amountValid || isLoading}
            >
              {isLoading ? "Recording…" : "Mark as paid"}
            </Button>
          </div>
        </div>
      }
    />
  );
}
